import { Spinner } from "@via/components/loading/loading.tsx";
import { useCheckRunEnvironmentDiskSpace } from "@via/api/run-environment-disk-space.api.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  type RunEnvironmentDiskSpace,
  type RunEnvironmentDiskSpaceResponseBody,
} from "common";
import { type JSX, useState } from "react";

type CheckResult =
  | { ok: true; space: RunEnvironmentDiskSpace }
  | { ok: false; error: string; at: Date };

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];

/**
 * Bytes in binary units, one decimal. Its own formatter rather than `formatFileSize`, which stops at TB:
 * a cluster filesystem is often petabytes.
 */
export const formatDiskBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const i = Math.min(
    UNITS.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  const value = bytes / Math.pow(1024, i);
  return `${0 === i ? value : value.toFixed(1)} ${UNITS[i]}`;
};

/** The folder's own size line, or why it has none. `canMeasureFolder` is false for a shared run's viewer. */
export const directoryUsageText = (
  space: RunEnvironmentDiskSpace,
  canMeasureFolder = true
): string => {
  const { directory } = space;
  if ("measured" === directory.kind) {
    if ("filesystem" === directory.source) {
      return `This folder holds ${formatDiskBytes(directory.bytes)}, the filesystem's own count, as the folder is its mount point.`;
    }
    return `This folder holds ${directory.complete ? "" : "at least "}${formatDiskBytes(directory.bytes)}.`;
  }
  switch (directory.reason) {
    case "not_requested":
      if (!canMeasureFolder) {
        return "The folder's own size is measured only for the run's owner.";
      }
      return "The folder's own size is not measured unless you ask, since walking a large folder can take up to 20 seconds.";
    case "path_missing":
      if (!canMeasureFolder) {
        return "The folder does not exist yet, so the space shown is for the filesystem it would be created on.";
      }
      return `The folder does not exist yet, so the space shown is for ${space.checkedPath}, where it would be created.`;
    case "timed_out":
      return "The folder's own size was not measured: it took longer than 20 seconds.";
    case "no_timeout_command":
      return "The folder's own size was not measured: the host has no timeout command to bound it.";
  }
};

/** The bar's color: amber from 80% used, red from 95%. */
const barColor = (usedPercent: number): string =>
  usedPercent >= 95
    ? "bg-rose-500"
    : usedPercent >= 80
      ? "bg-amber-500"
      : "bg-emerald-500";

/**
 * "Check disk space" for a path on a saved run environment: free and used space on the filesystem
 * under the path, and the size of the folder itself. Used by the profile's Directories card and the
 * run page's Execution Directory.
 */
export function DiskSpaceCheck({
  runEnvironmentId,
  path,
  testId = "disk-space-check",
  disabledReason,
  asUserId,
  accessNote,
  runId,
}: {
  runEnvironmentId: number | null | undefined;
  path: string | null | undefined;
  testId?: string;
  /** Shown instead of the button's work when the check cannot run here (an unsaved environment). */
  disabledReason?: string;
  /**
   * Whose access the check uses. The run page passes the run's owner, whose environment it is; without it the check
   * uses the profile page's impersonation, which outlives that page until a reload.
   */
  asUserId?: number | null;
  /** Says whose access the check uses, beside the button, when that is not obvious (a shared run's viewer). */
  accessNote?: string;
  /**
   * A shared run's viewer: the server checks this run's saved environment and execution directory as its owner,
   * and `runEnvironmentId`, `path` and `asUserId` are not sent. The folder's own size is not offered.
   */
  runId?: number | null;
}): JSX.Element {
  const { mutate, isPending } = useCheckRunEnvironmentDiskSpace();
  const [result, setResult] = useState<CheckResult | null>(null);
  const unavailable =
    disabledReason ??
    (runId || runEnvironmentId ? null : "Choose a run environment first.");

  /** `measureFolder` also walks the folder with `du`, which the first check leaves out to answer at once. */
  const check = (measureFolder = false): void => {
    if (!runId && !runEnvironmentId) return;
    mutate(
      runId
        ? { runId, measureFolder }
        : {
            runEnvironmentId,
            path: (path ?? "").trim(),
            measureFolder,
            asUserId,
          },
      {
        onSuccess: (response: RunEnvironmentDiskSpaceResponseBody) => {
          setResult(
            response.success
              ? { ok: true, space: response }
              : { ok: false, error: response.error, at: new Date() }
          );
        },
        onError: () => {
          setResult({
            ok: false,
            error: "The request did not reach the server.",
            at: new Date(),
          });
        },
      }
    );
  };

  return (
    <div className="mt-2" data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-button`}
        className="rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
        disabled={isPending || null !== unavailable}
        title={unavailable ?? undefined}
        onClick={() => check()}>
        <Spinner loading={isPending}>Check disk space</Spinner>
      </button>
      {unavailable ? (
        <span className="ml-2 text-xs text-gray-500">{unavailable}</span>
      ) : accessNote ? (
        <span
          data-testid={`${testId}-access-note`}
          className="ml-2 text-xs text-gray-500">
          {accessNote}
        </span>
      ) : null}

      {result && !result.ok ? (
        <p
          data-testid={`${testId}-result`}
          className="mt-2 text-xs text-rose-500">
          Failed: {result.error} · checked {result.at.toLocaleTimeString()}
        </p>
      ) : null}

      {result?.ok ? (
        <div
          data-testid={`${testId}-result`}
          className="mt-2 max-w-md rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
          <div className="flex justify-between font-medium text-gray-900">
            <span>{formatDiskBytes(result.space.availableBytes)} free</span>
            <span>
              {formatDiskBytes(result.space.usedBytes)} used of{" "}
              {formatDiskBytes(result.space.totalBytes)} (
              {result.space.usedPercent}%)
            </span>
          </div>
          <div
            className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="meter"
            aria-label="Disk used"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={result.space.usedPercent}>
            <div
              className={classNames(
                "h-full",
                barColor(result.space.usedPercent)
              )}
              style={{
                width: `${Math.min(100, Math.max(0, result.space.usedPercent))}%`,
              }}
            />
          </div>
          <p className="mt-1.5">
            {directoryUsageText(result.space, !runId)}
            {!runId &&
            "not_measured" === result.space.directory.kind &&
            "not_requested" === result.space.directory.reason ? (
              <button
                type="button"
                data-testid={`${testId}-measure-folder`}
                className="ml-1 font-semibold text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                disabled={isPending}
                onClick={() => check(true)}>
                {isPending ? "Measuring…" : "Measure folder size"}
              </button>
            ) : null}
          </p>
          <p className="mt-1 text-gray-500">
            {/* A shared run's viewer is not told the owner's filesystem or mount point. */}
            {result.space.filesystem
              ? `Filesystem ${result.space.filesystem} mounted on ${result.space.mountPoint} · `
              : ""}
            checked {new Date(result.space.checkedAt).toLocaleTimeString()}
          </p>
        </div>
      ) : null}
    </div>
  );
}
